import type { SkillType } from "../types";
import { kSkillCardZh } from "./glossary";

export type SkillTiming =
  | "action"            // Action: ... (spend an action)
  | "beforeRoll"        // play before a die is rolled
  | "attackReroll"      // play during any attack to reroll a die
  | "beforeCheck"       // play before any cards are added to a skill check
  | "afterTotal"        // play after calculating the total of a skill check
  | "wouldDefeatSelf"   // when you would be defeated
  | "actionStepStart";  // treachery: play at the start of your Action step

export interface SkillDef {
  id: string;
  type: SkillType;
  value: number;
  /** Chinese display name (see glossary.ts). */
  name: string;
  /** Original English card name. */
  en: string;
  timing: SkillTiming;
  text: string;
}

const D = (type: SkillType, value: number, name: string, timing: SkillTiming, text: string): SkillDef =>
  ({ id: `${type}-${value}`, type, value, name: kSkillCardZh[name], en: name, timing, text });

const kDefs: SkillDef[] = [
  D("influence", 1, "Coordinated Effort", "action", "行动：选另一名角色，该角色执行 1 个行动。"),
  D("influence", 2, "Coordinated Effort", "action", "行动：选另一名角色，该角色执行 1 个行动。"),
  D("influence", 3, "Nothing to Hide", "beforeCheck", "检定加牌前打出：本次检定所有技能卡正面朝上加入（混沌牌除外）。"),
  D("influence", 4, "Nothing to Hide", "beforeCheck", "检定加牌前打出：本次检定所有技能卡正面朝上加入（混沌牌除外）。"),
  D("influence", 5, "Inspiring Speech", "action", "行动：掷骰，≥3 则 +1 理智，否则 -1 理智。"),
  D("lore", 1, "Shrivelling", "action", "行动：选任意空间 1 个敌人并掷骰；若为深潜者 +2；≥6 击败它。"),
  D("lore", 2, "Shrivelling", "action", "行动：选任意空间 1 个敌人并掷骰；若为深潜者 +2；≥6 击败它。"),
  D("lore", 3, "Lesser Banishment", "action", "行动：选一个空间，把其中所有深潜者移到同一个相邻空间。"),
  D("lore", 4, "Lesser Banishment", "action", "行动：选一个空间，把其中所有深潜者移到同一个相邻空间。"),
  D("lore", 5, "Forced Learning", "beforeCheck", "检定加牌前打出：选一种非背叛技能，本次检定中它成为支持技能。"),
  D("observation", 1, "Watch and Learn", "action", "行动：从任意非背叛牌堆共抽 2 张技能卡。"),
  D("observation", 2, "Watch and Learn", "action", "行动：从任意非背叛牌堆共抽 2 张技能卡。"),
  D("observation", 3, "Keen Insight", "beforeRoll", "掷骰前打出，二选一：结果 +2，或结果 -2。"),
  D("observation", 4, "Keen Insight", "beforeRoll", "掷骰前打出，二选一：结果 +2，或结果 -2。"),
  D("observation", 5, "Preparation", "beforeCheck", "检定加牌前打出：本次检定不加入混沌牌。"),
  D("strength", 1, "Combat Training", "attackReroll", "任意攻击中打出：重投骰子。"),
  D("strength", 2, "Combat Training", "attackReroll", "任意攻击中打出：重投骰子。"),
  D("strength", 3, "Rampage", "action", "行动二选一：对本空间敌人至多攻击 4 次；或执行你某个武器物品的行动至多 3 次。"),
  D("strength", 4, "Rampage", "action", "行动二选一：对本空间敌人至多攻击 4 次；或执行你某个武器物品的行动至多 3 次。"),
  D("strength", 5, "Shrug It Off", "wouldDefeatSelf", "你将被击败时打出：防止你被击败。"),
  D("will", 1, "Persistence", "action", "行动：弃至多 5 张手牌，然后从同一个非背叛牌堆抽等量的牌。"),
  D("will", 2, "Persistence", "action", "行动：弃至多 5 张手牌，然后从同一个非背叛牌堆抽等量的牌。"),
  D("will", 3, "Determination", "afterTotal", "检定总值算出后打出：总值 +2 或 -2。"),
  D("will", 4, "Determination", "afterTotal", "检定总值算出后打出：总值 +2 或 -2。"),
  D("will", 5, "True Grit", "action", "行动：执行 2 个行动。每回合限 1 张「钢铁意志」。"),
  D("treachery", 1, "Reinforcements", "action", "行动：在深渊生成 1 个深潜者；若深渊中 ≥4 个，把深渊所有怪物移到你选择的一个水域。"),
  D("treachery", 2, "Call to Action", "action", "行动二选一：激活你所在空间的每个深潜者；或激活任意空间的 1 个深潜者。"),
  D("treachery", 3, "Siren Song", "action", "行动：冒险放置 1 名乘客。"),
  D("treachery", 4, "Ransack", "actionStepStart", "行动步骤开始时打出：在礼拜堂 -1 理智；在厨房 -1 食物；在锅炉房 -1 燃料。然后结束行动步骤。"),
  D("treachery", 5, "Perform Rites", "actionStepStart", "行动步骤开始时打出：按你选择的顺序结算母神海德拉与父神大衮激活图标。然后结束行动步骤。"),
];

export const kSkillDefs: Record<string, SkillDef> = Object.fromEntries(kDefs.map(d => [d.id, d]));

/** Card counts per value in every skill deck (1x7, 2x5, 3x4, 4x3, 5x2). */
export const kSkillValueCounts: Record<number, number> = { 1: 7, 2: 5, 3: 4, 4: 3, 5: 2 };

export function skillDef(def: string): SkillDef {
  const d = kSkillDefs[def];
  if (!d) throw new Error(`unknown skill card ${def}`);
  return d;
}
